import { Box, Grid, Stack, Theme, Typography, useTheme } from "@mui/material";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  selectOpponents,
  selectOpponentsSelected,
  updateOpponentSelection,
  selectCategoriesSelected,
} from "@/global/store/data/gameFilters";
import { useAppStore } from "@/global/store";
import { Image } from "@/shared/base/components/Image/Image";
import { selectEvents } from "@/global/store/data/purchaseSeats/events";
import { useTeamConfig } from "@/global/hooks/useTeamConfig";
import { filterOpponentsCategoryBased } from "../utils.gameFilters";

export function OpponentSelector(props: { title?: string }) {
  const { purchaseSeatConfigs } = useTeamConfig();
  const dispatch = useDispatch();
  const opponents = useAppStore(selectOpponents);
  const selectedOpponents = useAppStore(selectOpponentsSelected);
  const selectedCategories = useAppStore(selectCategoriesSelected);
  const events = useAppStore(selectEvents);
  const [opponentsState, setOpponentsState] = useState(opponents);

  const handleSelect = (opponentName: string) => {
    dispatch(updateOpponentSelection(opponentName));
  };

  useEffect(() => {
    setOpponentsState(
      filterOpponentsCategoryBased({
        events,
        opponents,
        selectedCategories,
      })
    );
  }, [selectedCategories, events, opponents]);

  return (
    <Box maxHeight={"450px"} overflow={"auto"} p={"2px"}>
      {props?.title ? (
        <Box>
          <Typography
            variant="subtitle1"
            fontWeight={"700"}
            color="text.primary"
          >
            {props?.title}
          </Typography>
        </Box>
      ) : null}

      <Box mt={2}>
        <Grid container spacing={3}>
          {opponentsState.map((itm, idx) => {
            let logoUrl = itm.opponentUrl;

            if (purchaseSeatConfigs?.useBackgroundUrlForEventLogo) {
              logoUrl = events.find((e) => {
                return e.opponentName === itm?.opponentName;
              })?.backgroundUrl as string;
            }

            return (
              <Grid key={idx} item xs={6} md={3}>
                <Opponent
                  name={itm?.opponentName}
                  logoUrl={logoUrl}
                  isSelected={selectedOpponents.includes(itm?.opponentName)}
                  onSelect={handleSelect}
                />
              </Grid>
            );
          })}
        </Grid>
      </Box>
    </Box>
  );
}

interface OpponentProps {
  name: string;
  logoUrl: string;
  isSelected: boolean;
  onSelect: (id: string) => void;
}

function Opponent(props: OpponentProps) {
  const theme = useTheme();
  const styles = useStyles(theme, { isSelected: props.isSelected });

  return (
    <Box onClick={() => props.onSelect(props.name)} sx={styles.opponentCard}>
      <Stack justifyContent={"center"} alignItems={"center"}>
        {props.logoUrl ? (
          <Image src={props.logoUrl} alt={props.name} width={48} height={48} />
        ) : null}
      </Stack>
      <Box textAlign={"center"} mt={2}>
        <Typography
          variant="body2"
          color="text.primary"
          fontWeight={600}
          lineHeight={1.3}
        >
          {props.name}
        </Typography>
      </Box>
    </Box>
  );
}

function useStyles(theme: Theme, state: { isSelected: boolean }) {
  return {
    opponentCard: {
      outline: `${state.isSelected ? "2px" : "1px"} solid ${
        state.isSelected ? theme.palette.primary.main : theme.palette.divider
      }`,
      borderRadius: 0.8,
      padding: 2,
      transition: "all 0.3s ease",
      cursor: "pointer",
      "&:hover": {
        backgroundColor: theme.palette.action.hover,
      },
      minHeight: "132px",
    },
  };
}
